import React, { forwardRef, JSX, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import Select, { components } from "react-select";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs } from "../../Styles";
import { CreateNewFieldStyle, CreateNewFieldType, NewFieldStyles, NewFieldTypes, CreateListStyle, ListStyle_Types, SelectFieldOption_Values, MenuFloats } from "../../Definitions";
import { NewSelectHandles, NewSelectListHandles } from "../../Definitions";

// #region | NewSelect
interface newSelectProps {
  options: SelectFieldOption_Values[]
  onChange?: (selectedOption: SelectFieldOption_Values | undefined) => void;
  onClear?: () => void
  placeholder?: string
  header?: string
  startEnhancer?: JSX.Element
  isRequired?: boolean
  isSearchable?: boolean
  isClearable?: boolean
  isDisabled?: boolean
  istableHeaderCell?: boolean
  fieldOverrides?: {
    fieldStyle?: NewFieldStyles
    fieldType?: NewFieldTypes
    fieldWidth?: string
    menuFloat?: MenuFloats
    menuWidth?: string
    focus_view?: boolean,
    spliceBorder?: number,
  }
  valueOverrides?: {
    defaultValue?: SelectFieldOption_Values;
    maxValueLength?: number;
  }
}


const capitalizeFirstLetter = (text: string | undefined | null): string => {
  if (typeof text !== 'string' &&  text) {
    return text;
  }
  if (typeof text !== 'string' || text.length === 0) {
    return '';
  }
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/** NewSelect
 * @param options The list of selectable options.
 * @param onChange Callback function triggered when an option is selected.
 * @param placeholder The placeholder text for the select field.
 * @param header The header text displayed above the select field.
 * @param startEnhancer An optional icon or element displayed inside the select field.
 * @param isRequired Specifies whether the field is required.
 * @param isSearchable Enables search functionality within the select dropdown.
 * @param isClearable Allows clearing the selected option.
 * @param isDisabled Disables the select field.
 * @param fieldOverrides Custom styles and settings for the select field.
 * @param valueOverrides Default selected option.
 * @returns A styled dropdown react-select component.
 */
const _NewSelect: React.ForwardRefRenderFunction<NewSelectHandles, newSelectProps> = ({options, onChange, onClear, placeholder, header, startEnhancer, isRequired=false, isSearchable=false, isClearable=false, isDisabled=false, istableHeaderCell=false, fieldOverrides, valueOverrides}:newSelectProps, ref) => {
  const {t} = useTranslation()
  const [isContainerHovered, setIsContainerHovered] = useState<boolean>(false); 
  const [isError, setError] = useState(false)
  const [selectValue, setSelectValue] = useState<SelectFieldOption_Values | undefined>(valueOverrides?.defaultValue || undefined);
  

  useImperativeHandle(ref, () => ({
    setError(state=true) {
      setError(state)
    },
    value: selectValue ?? ({} as SelectFieldOption_Values),
    setValue: (e?: SelectFieldOption_Values) => { setSelectValue(e); }
  }));

  const spliceBorder = fieldOverrides?.spliceBorder;

  // --- Custom MenuList der respekterer spliceBorder
  const MenuListWithDivider = (props: any) => {
    const { children } = props;
    const modifiedChildren = React.Children.toArray(children);

    if (typeof spliceBorder === "number" && spliceBorder >= 0 && spliceBorder < modifiedChildren.length) {
      modifiedChildren.splice(
        spliceBorder,
        0,
        <div
          key="divider"
          style={{
            width: "100%",
            borderBottom: "2px solid " + colors.greyDarkPrimary,
            margin: "10px 0",
          }}
        />
      );
    }

    return <components.MenuList {...props}>{modifiedChildren}</components.MenuList>;
  };

  // Placeholder
  const _placeholder = placeholder ? placeholder + "..." : "...";

  // FieldStyle
  const { primaryColor, secondaryColor, fontColor, headerColor } = useMemo(() => {
    var _Style = CreateNewFieldStyle(fieldOverrides?.fieldStyle)
    return _Style
  }, [fieldOverrides?.fieldStyle])

  // FieldType
  const { height, containerPaddingTop, containerPaddingRight, containerPaddingBottom, containerPaddingLeft, indicatorPadding, indicatorSeparatorDisplay, dropdownIndicator, placeholderDisplay } = useMemo(() => {
    var _Style = CreateNewFieldType(fieldOverrides?.fieldType, fieldOverrides?.focus_view)
    return _Style
  }, [fieldOverrides?.fieldType, fieldOverrides?.focus_view])

  // Additional styling
  const _headerShadow = fieldOverrides&&fieldOverrides.fieldStyle === NewFieldStyles.greyTertiary ? undefined : {...fonts.textShadow} 
  const _fieldWidth = fieldOverrides&&fieldOverrides.fieldWidth !== undefined ? fieldOverrides.fieldWidth : fieldOverrides&&fieldOverrides.focus_view ? "100%" : "280px";
  
  const _menuWidth = 
    fieldOverrides&&fieldOverrides.menuWidth ? fieldOverrides.menuWidth : fieldOverrides&&fieldOverrides.focus_view ? "100%" : _fieldWidth

  const _menuFloat = {
    right: (fieldOverrides&&fieldOverrides.menuFloat === MenuFloats.right) ? "0" : "unset",
    left: (fieldOverrides&&fieldOverrides.menuFloat === MenuFloats.left) ? "0" : (fieldOverrides&&fieldOverrides.menuFloat === MenuFloats.center) ? "50%" : "unset",
    transform: (fieldOverrides&&fieldOverrides.menuFloat === MenuFloats.center) ? "translateX(-50%)" : "unset"
  }

  const _containerPadding = (
    startEnhancer ? "2px 8px 2px 36px"
    : `${containerPaddingTop}px ${containerPaddingRight}px ${containerPaddingBottom}px ${containerPaddingLeft}px`
  )

  // Set value to defaultValue
  useEffect(() => {
    setSelectValue(valueOverrides?.defaultValue);
  }, [valueOverrides?.defaultValue]);

  // Handle option select change
  const handleChange = (selectedOption: SelectFieldOption_Values | null) => {
    setSelectValue(selectedOption ?? undefined);
    if (onChange) {
      onChange(selectedOption ?? undefined);
    }
  };

  const OptionWithIcon = (props: any) => {
    const { data, children, ...rest } = props;
    return (
      <components.Option {...rest}>
        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {data.icon}
          {children}
        </span>
      </components.Option>
    );
  };

    const LimitedInput = (props: any) => {
      const max = valueOverrides?.maxValueLength ?? undefined;
      return (
        <components.Input
          {...props}
          maxLength={max}
        />
      );
    };
  
    const handleInputChange = (inputValue: string) => {
      const max = valueOverrides?.maxValueLength;
      if (!max) return inputValue;
      return inputValue.slice(0, max);
    };

  return ( 
    <div style={{width: _fieldWidth, cursor: isDisabled? "not-allowed" : "pointer"}}>
      {header && <p style={{ ..._headerShadow, ...fonts.header, ...(fieldOverrides?.focus_view ? fonts.fontSize3 : fonts.fontSize4), color: headerColor, padding: "0px 0px 2px 10px", whiteSpace: "nowrap" }}>{header}</p>}
      <div style={{position: "relative"}} onMouseEnter={() => istableHeaderCell&&setIsContainerHovered(true)} onMouseLeave={() => istableHeaderCell&&setIsContainerHovered(false)} >
        <Select<SelectFieldOption_Values>
          key={selectValue?.id}
          value={selectValue}
          onChange={handleChange} 
          defaultValue={valueOverrides?.defaultValue}
          placeholder={_placeholder}
          options={options} 
          required={isRequired}
          isSearchable={isSearchable}
          isClearable={isClearable}
          formatOptionLabel={(option, { context }) => (
            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {option.label ? capitalizeFirstLetter(option.label) : ''}
              {option.secondaryLabel && (
                <span style={{ opacity: 0.5 }}>
                  {option.secondaryLabel}
                </span>
              )}
            </span>
          )}
          isDisabled={isDisabled}
          menuPortalTarget={document.body} 
          maxMenuHeight={200}
          menuPlacement={"auto"}
          noOptionsMessage={() => t("noOptions", {defaultValue: "No Options"})}
          getOptionLabel={(option) => `${option.label} ${option.secondaryLabel ?? ""}`}
          getOptionValue={(option) => option.label}
          onInputChange={handleInputChange}
          components={{
            Option: OptionWithIcon,
            MenuList: MenuListWithDivider,
            Input: LimitedInput
          }}
          styles={{
            container: (baseStyles) => ({
              ...baseStyles,
              width: _fieldWidth,
              height: `${height}px`,
              ...fonts.paragraph,
              ...(fieldOverrides&&fieldOverrides.focus_view? fonts.fontSize3 : fonts.fontSize4),
              zIndex: "99"
            }),
            control: (baseStyles) => ({
              ...baseStyles,
              backgroundColor: fieldOverrides&&fieldOverrides.fieldStyle === NewFieldStyles.transparent ? colors.transparent : isDisabled ? primaryColor+52 : primaryColor,
              border: "none",
              borderRadius: "10px",
              boxShadow: "none",
              cursor: "pointer",
              minHeight: "unset",
            }),
            valueContainer: (baseStyles) => ({
              ...baseStyles,
              padding: _containerPadding,
            }),
            input: (baseStyles) => ({
              ...baseStyles,
              lineHeight: "1",
              margin: "0px",
              padding: "0px",
              color: fontColor,
            }),
            menu: (baseStyles) => ({
              ...baseStyles,
              ..._menuFloat,
              backgroundColor: primaryColor,
              borderRadius: "10px",
              width: _menuWidth,
            }),
            menuList: (baseStyles) => ({
              ...baseStyles,
              backgroundColor: primaryColor,
              borderRadius: "10px",
              border: "5px solid " + primaryColor,
              borderLeft: "none",
              borderRight: "none",
              padding: "10px",
              overflowY: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: secondaryColor + colors.transparent,
              ...fonts.paragraph,
              ...(fieldOverrides&&fieldOverrides.focus_view? fonts.fontSize3 : fonts.fontSize4),
            }),
            option: (baseStyles, state) => ({
              ...baseStyles,
              borderRadius: "10px",
              margin: "3px 0px",
              color: fontColor,
              display: "flex",
              alignItems: "center",
              height: `${height}px`,
              backgroundColor: state.isSelected ? secondaryColor : primaryColor,
              transition: "background-color 0.3s ease",
              "&:hover": {
                backgroundColor: fieldOverrides&&fieldOverrides.focus_view ? "unset" : secondaryColor,
                cursor: "pointer"
              }
            }),
            singleValue: (baseStyles) => ({
              ...baseStyles,
              color: isDisabled ? fontColor + "ab" : fontColor,
              overflow: undefined,
              margin: "0px"
            }),
            indicatorsContainer: (baseStyles) => ({
              ...baseStyles,
              height: `${height}px`,
            }), 
            indicatorSeparator: (baseStyles) => ({
              ...baseStyles,
              display: indicatorSeparatorDisplay,
              backgroundColor: isDisabled ? secondaryColor+"ab" : secondaryColor
            }), 
            dropdownIndicator: (baseStyles) => ({
              ...baseStyles,
              display: dropdownIndicator,
              padding: `${indicatorPadding}px`,
              color: isDisabled ? secondaryColor+"ab" : secondaryColor,
              "&:hover": {
                color: fontColor
              }
            }), 
            clearIndicator: (baseStyles) => ({
              ...baseStyles,
              padding: `${indicatorPadding}px`,
              color: secondaryColor,
              "&:hover": {
                color: fontColor
              }
            }), 
            placeholder: (baseStyles) => ({
              ...baseStyles,
              display: placeholderDisplay,
              color: fontColor,
              whiteSpace: "nowrap",
              filter: "opacity(0.5)",
              "::first-letter": {
                textTransform: "uppercase"
              }
            }),
            menuPortal: (baseStyles) => ({
              ...baseStyles,
              zIndex: "99999999999999"
            })
          }}
        />
        {istableHeaderCell
          ? <div style={{position: "absolute", height: "20px", top: "50%", left: "50%", transform: "translate(-50%, -50%)", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", filter: isContainerHovered ? "opacity(0.3)" : "opacity(1)", transition: "filter 0.2s ease"}}>
              <glyphs.Add size="15px"/> 
            </div>
          : <></>
        }
        {startEnhancer && (
          <div style={{position: "absolute", height: "20px", top: "50%", transform: "translateY(-50%)", left: "8px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", zIndex: "99"}}>
            {startEnhancer}
          </div>
        )}
        {isRequired && !selectValue 
          ? <div style={{ position: "absolute", zIndex: "999", height: "22px", top: "50%", right: "37px", transform: "translateY(-50%)", display: "grid", gridAutoFlow: "column", gridAutoColumns: "1fr", padding: "4px 8px", alignItems: "center" }}>
              <glyphs.Error fillColor={colors.systemRed} />
            </div>
          : <></>
        }
        <div style={{ position: "absolute", pointerEvents: "none", zIndex: "999", height: "22px", top: "50%", right: (isClearable && selectValue) ? "57px" : (isRequired && !selectValue) ? "60px" : "37px", transform: "translateY(-50%)", display: "grid", gridAutoFlow: "column", gridAutoColumns: "1fr", padding: "4px 8px", alignItems: "center" }}>
          { isSearchable && (fieldOverrides&&fieldOverrides.fieldType === NewFieldTypes.primaryField) && <glyphs.Search fillColor={secondaryColor} />}
        </div>
      </div>
    </div> 
  )
}
const NewSelect = forwardRef(_NewSelect);
export default NewSelect
// #endregion

// #region | NewSelectList
interface newSelectListProps {
  options: SelectFieldOption_Values[][]
  onChanges?: ((selectedOption: SelectFieldOption_Values | undefined) => void)[]
  placeholders: string[]
  headers?: string[]
  startEnhancers?: JSX.Element[]
  isRequired?: boolean[]
  isSearchable?: boolean
  isClearable?: boolean
  isDisabled?: boolean[]
  fieldOverrides?: {
    fieldStyle?: NewFieldStyles
    fieldType?: NewFieldTypes
    fieldWidth?: string
    menuFloat?: MenuFloats
    focus_view?: boolean
  }
  valueOverrides?: {
    defaultValues?: SelectFieldOption_Values[]
  }
  listOverrides?: {
    listGap?: string
    listStyle?: ListStyle_Types
  }
}
/**
 * NewSelectList
 * @param options A list of selectable options for each select field.
 * @param onChanges Callback functions triggered when an option is selected for each select field.
 * @param placeholders Placeholder texts for each select field.
 * @param headers Header texts displayed above each select field.
 * @param startEnhancers Icons or elements displayed inside each select field.
 * @param isRequired Specifies whether each field is required.
 * @param isSearchable Enables search functionality within all select fields.
 * @param isClearable Allows clearing selected options in all fields.
 * @param isDisabled Disables the select fields.
 * @param fieldOverrides Custom styles and settings for each select field.
 * @param valueOverrides Default selected values for each select field.
 * @param listOverrides Custom styles and settings for the list layout.
 * @param ref A reference for accessing input values and elements.
 * @returns A list of styled dropdown react-select components. 
 */
const _NewSelectList:React.ForwardRefRenderFunction<NewSelectListHandles, newSelectListProps> = ({options, onChanges, placeholders, headers, startEnhancers, isRequired=[false], isSearchable=false, isClearable=false, isDisabled=[false], fieldOverrides, valueOverrides, listOverrides}:newSelectListProps, ref) => {
  const selectRefs = useRef<(NewSelectHandles | null)[]>([]);
  const _listGap = listOverrides&&listOverrides.listGap ? { gap: listOverrides.listGap } : { gap: "5px" };
  const { listDirection } = useMemo(() => CreateListStyle(listOverrides&&listOverrides.listStyle), [listOverrides])

  useImperativeHandle(ref, ()=>({
    setError(index=0,state) { 
      if (selectRefs.current && selectRefs.current[index]) selectRefs.current[index].setError(state)
    },
    value(index=0): SelectFieldOption_Values | undefined {
      if (selectRefs.current && selectRefs.current[index]) return selectRefs.current[index]?.value
      return undefined
    },
    refs:selectRefs
  }))

  const Item_List = useMemo(() => {
    return placeholders?.map((_placeholders, index) => {
      const _options = options && options[index] ? options[index] : []
      const _onChanges = onChanges && onChanges[index] ? onChanges[index] : undefined
      const _headers = headers&&headers[index] ? headers[index] : ""
      const _defaultValues = valueOverrides&&valueOverrides.defaultValues && valueOverrides.defaultValues[index] ? valueOverrides.defaultValues[index] : undefined
      const _startEnhancers = startEnhancers&&startEnhancers[index] ? startEnhancers[index] : undefined
      const _isRequired = isRequired && isRequired[index] ? isRequired[index] : undefined
      const _isDisabled = isDisabled && isDisabled[index] ? isDisabled[index] : undefined
      const _overrides = fieldOverrides || {};

      var view = [
        <NewSelect 
          key={"List_Select_" + index}
          ref={(el) => el ? selectRefs.current[index] = el : null}
          placeholder={_placeholders}
          options={_options}     
          onChange={_onChanges}
          header={_headers}
          startEnhancer={_startEnhancers}
          isRequired={_isRequired}
          isSearchable={isSearchable}
          isClearable={isClearable}
          isDisabled={_isDisabled}
          fieldOverrides={_overrides}
          valueOverrides={{
            defaultValue: _defaultValues
          }}
        />
      ];

      return view;
    });
  }, [placeholders, options, onChanges, headers, valueOverrides, startEnhancers, isRequired, isDisabled, fieldOverrides, isSearchable, isClearable]);

  return (
    <div style={{...listDirection as React.CSSProperties, ..._listGap}}>
      {Item_List}
    </div>
  ) 
}
export const NewSelectList = forwardRef(_NewSelectList)
// #endregion